import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  orderService,
  type FoodOrder,
  type FoodOrderItem,
  CATEGORY_TYPES,
  CATEGORY_TYPE_LABELS,
  type CategoryType,
} from '@resort-os/api-client';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  DndContext,
  useDraggable,
  useDroppable,
  DragOverlay,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Start as StartIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Restaurant as RestaurantIcon,
  LocalBar as LocalBarIcon,
  Store as StoreIcon,
  RoomService as RoomServiceIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
  Timer as TimerIcon,
  HourglassEmpty as HourglassIcon,
  FilterList as FilterIcon,
  History as HistoryIcon,
  CheckCircle as ReadyIcon,
  RestaurantMenu as ServedIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { useAuth, usePermission, PageSection } from '@resort-os/ui';
import { useOrderWebSocket, type OrderEvent } from '../hooks/useOrderWebSocket';
import { playBellNotification } from '../utils/beep';
import DraggablePanel from '../components/DraggablePanel';

const ACCENT = '#2C3E2D';
const BORDER = '1px solid #E2DDD8';

type Department = 'all' | CategoryType;

const DEPARTMENTS: { id: Department; label: string; icon: React.ReactNode; color: string }[] = [
  { id: 'all', label: 'All', icon: null, color: ACCENT },
  {
    id: 'beverage',
    label: CATEGORY_TYPE_LABELS.beverage,
    icon: <LocalBarIcon sx={{ fontSize: 17 }} />,
    color: '#2E7D32',
  },
  {
    id: 'food',
    label: CATEGORY_TYPE_LABELS.food,
    icon: <RestaurantIcon sx={{ fontSize: 17 }} />,
    color: '#ED6C02',
  },
  {
    id: 'merchandise',
    label: CATEGORY_TYPE_LABELS.merchandise,
    icon: <StoreIcon sx={{ fontSize: 17 }} />,
    color: '#0288D1',
  },
  {
    id: 'service',
    label: CATEGORY_TYPE_LABELS.service,
    icon: <RoomServiceIcon sx={{ fontSize: 17 }} />,
    color: '#9C27B0',
  },
  {
    id: 'meal_packages',
    label: CATEGORY_TYPE_LABELS.meal_packages,
    icon: <ServedIcon sx={{ fontSize: 17 }} />,
    color: '#E65100',
  },
];

interface ItemWithOrder {
  item: FoodOrderItem;
  order: FoodOrder;
}

function isFloorServer(role?: string) {
  return role === 'waiter' || role === 'shift_supervisor';
}

/** The person currently holding the check. A transfer replaces assigned_server. */
function waiterHoldsOrder(order: FoodOrder, userId?: string) {
  if (!userId) return false;
  const holder = order.assigned_server || order.created_by;
  return holder === userId;
}

function SplitDisplay({ PageHeader }: { PageHeader: React.FC<{ title: string }> }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const storageKey = isFloorServer(user?.role)
    ? 'pos-department-filter-server'
    : 'pos-department-filter';
  const [departmentFilter, setDepartmentFilter] = useState<Department | null>(null);

  useEffect(() => {
    if (!user?.role) return;
    const stored = localStorage.getItem(storageKey) as Department | null;
    if (stored && DEPARTMENTS.some(dept => dept.id === stored)) {
      setDepartmentFilter(stored);
      return;
    }
    setDepartmentFilter(isFloorServer(user.role) ? 'all' : 'food');
  }, [storageKey, user?.role]);

  useEffect(() => {
    if (!user?.role || !departmentFilter) return;
    localStorage.setItem(storageKey, departmentFilter);
  }, [departmentFilter, storageKey, user?.role]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [transitioningId, setTransitioningId] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor),
  );

  const pendingRef = useRef<HTMLDivElement>(null);
  const preparingRef = useRef<HTMLDivElement>(null);
  const readyRef = useRef<HTMLDivElement>(null);
  const servedRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['orders'],
    queryFn: () => orderService.list({ page_size: 50 }),
    refetchInterval: 10_000,
  });

  const handleOrderEvent = (event: OrderEvent) => {
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    if (event.type === 'food.order.created' || event.items?.length) {
      playBellNotification();
    }
  };
  const { connected: wsConnected } = useOrderWebSocket(handleOrderEvent);

  const updateItemStatusMutation = useMutation({
    mutationFn: async ({
      itemId,
      status,
    }: {
      itemId: string;
      status: string;
      order: FoodOrder;
      item: FoodOrderItem;
    }) => {
      await orderService.updateItemStatus(itemId, status);
      return true;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['orders'] }),
  });

  const preparePermission = usePermission('orders.prepare');
  const markReadyPermission = usePermission('orders.mark_ready');
  const markServedPermission = usePermission('orders.mark_served');

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    console.log('DRAG_END:', { activeId: active?.id, overId: over?.id });
    if (!over) return;
    const itemId = String(active.id);
    const targetColumn = String(over.id);
    const found = allItems.find(({ item }) => item.id === itemId);
    if (!found) return console.log('ITEM_NOT_FOUND:', itemId);
    const { item, order } = found;
    console.log('DRAG:', { from: item.status, to: targetColumn, item: item.product_name });
    if (item.status === targetColumn) return;

    let newStatus: string | null = null;
    if (targetColumn === 'preparing' && item.status === 'pending' && preparePermission)
      newStatus = 'preparing';
    else if (
      targetColumn === 'ready' &&
      ['pending', 'preparing'].includes(item.status) &&
      markReadyPermission
    )
      newStatus = 'ready';
    else if (
      targetColumn === 'served' &&
      ['pending', 'preparing', 'ready'].includes(item.status) &&
      markServedPermission
    )
      newStatus = 'served';

    if (newStatus) {
      setTransitioningId(itemId);
      updateItemStatusMutation.mutate(
        { itemId, status: newStatus, order, item },
        {
          onSettled: () => setTransitioningId(null),
        },
      );
    }
  };

  const orders: FoodOrder[] = data?.data?.data || [];
  const todayStr = new Date().toISOString().split('T')[0];
  const waiterId = user?.role === 'waiter' ? user.id : undefined;

  const allItems: ItemWithOrder[] = useMemo(() => {
    const items: ItemWithOrder[] = [];
    orders.forEach(order => {
      const orderDate = new Date(order.created_at).toISOString().split('T')[0];
      if (orderDate !== todayStr) return;
      if (!['pending', 'confirmed', 'preparing', 'ready', 'served'].includes(order.status)) return;
      if (waiterId && !waiterHoldsOrder(order, waiterId)) return;
      order.items?.forEach(item => {
        items.push({ item, order });
      });
    });
    return items;
  }, [orders, todayStr, waiterId]);

  const getDepartment = (item: FoodOrderItem): Department =>
    (item.department as Department) || 'food';

  const filteredItems = useMemo(() => {
    return allItems.filter(({ item }) => {
      const dept = getDepartment(item);
      if (departmentFilter !== 'all' && dept !== departmentFilter) return false;
      return true;
    });
  }, [allItems, departmentFilter]);

  const pendingItems = filteredItems.filter(({ item }) => item.status === 'pending');
  const preparingItems = filteredItems.filter(({ item }) => item.status === 'preparing');
  const readyItems = filteredItems.filter(({ item }) => item.status === 'ready');
  const servedItems = filteredItems.filter(({ item }) => item.status === 'served');

  const departmentCounts = useMemo(() => {
    const counts: Record<Department, number> = {
      all: allItems.length,
      food: 0,
      beverage: 0,
      merchandise: 0,
      service: 0,
      meal_packages: 0,
    };
    for (const { item } of allItems) {
      const dept = getDepartment(item);
      counts[dept] = (counts[dept] || 0) + 1;
    }
    return counts;
  }, [allItems]);

  useEffect(() => {
    const adjustColumnHeights = () => {
      const viewportHeight = window.innerHeight;
      const headerHeight = 180; // Approximate height of header + filters
      const maxColumnHeight = viewportHeight - headerHeight;

      [pendingRef, preparingRef, readyRef, servedRef].forEach(ref => {
        if (ref.current) {
          ref.current.style.maxHeight = `${maxColumnHeight}px`;
        }
      });
    };

    adjustColumnHeights();
    window.addEventListener('resize', adjustColumnHeights);
    return () => window.removeEventListener('resize', adjustColumnHeights);
  }, []);

  if (isLoading || !departmentFilter) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: '#FAF9F7',
        }}
      >
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <Box
      sx={
        isFullscreen
          ? {
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              bgcolor: '#FAFAF7',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
            }
          : { minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }
      }
    >
      <PageSection>
        <PageHeader
          title={departmentFilter === 'all' ? "Order's Display" : departmentFilter.toUpperCase()}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, px: 0.5 }}>
          <Box data-tour="livestatus-chip">
            <Tooltip title="WebSocket connection status" arrow>
              <Chip
                size="small"
                icon={
                  wsConnected ? (
                    <HubOutlinedIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <HubOutlinedIcon sx={{ fontSize: 14 }} />
                  )
                }
                label={wsConnected ? 'Live' : 'Polling'}
                sx={{
                  fontSize: '0.6rem',
                  height: 22,
                  color: wsConnected ? '#2E7D32' : '#E65100',
                  bgcolor: wsConnected ? '#E8F5E9' : '#FFF3E0',
                  borderRadius: 1,
                  '& .MuiChip-icon': { color: wsConnected ? '#2E7D32' : '#E65100' },
                }}
              />
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} arrow>
            <IconButton
              size="small"
              onClick={() => setIsFullscreen(!isFullscreen)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: 0,
                border: '1px solid #E4DFD9',
                color: '#6B6560',
                bgcolor: '#F5F3F0',
                '&:hover': { bgcolor: ACCENT, color: '#fff', borderColor: ACCENT },
              }}
            >
              {isFullscreen ? (
                <FullscreenExitIcon sx={{ fontSize: 16 }} />
              ) : (
                <FullscreenIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {departmentFilter !== 'all' && (
            <Typography
              sx={{
                position: 'absolute',
                top: '70%',
                left: '50%',
                transform: 'translate(-50%, -50%)',

                fontSize: '8rem',
                fontWeight: 700,
                color: 'rgba(0,0,0,0.03)',
                pointerEvents: 'none',
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {DEPARTMENTS.find(d => d.id === departmentFilter)?.label}
            </Typography>
          )}
          <DndContext
            sensors={sensors}
            onDragStart={(e: { active: { id: string | number } }) =>
              setActiveId(String(e.active.id))
            }
            onDragEnd={(e: DragEndEvent) => {
              handleDragEnd(e);
              setActiveId(null);
            }}
            collisionDetection={closestCenter}
          >
            <Box
              data-tour="kanban-columns"
              sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', height: '100%' }}
            >
              <Column
                title="Pending"
                icon={<HourglassIcon sx={{ fontSize: 18 }} />}
                count={pendingItems.length}
                columnStatus="pending"
                dataTour="item-actions"
              >
                <ColumnContent ref={pendingRef}>
                  {pendingItems.map(({ item, order }) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      order={order}
                      onStartPrep={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'preparing',
                          order,
                          item,
                        })
                      }
                      onMarkReady={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'ready',
                          order,
                          item,
                        })
                      }
                      onMarkServed={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'served',
                          order,
                          item,
                        })
                      }
                      onComplete={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'completed',
                          order,
                          item,
                        })
                      }
                      onCancel={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'cancelled',
                          order,
                          item,
                        })
                      }
                      isTransitioning={item.id === transitioningId}
                    />
                  ))}
                  {pendingItems.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: '#9B958F' }}>
                      <Typography sx={{ fontSize: '0.75rem' }}>No pending items</Typography>
                    </Box>
                  )}
                </ColumnContent>
              </Column>

              <Column
                title="In Progress"
                icon={<TimerIcon sx={{ fontSize: 18 }} />}
                count={preparingItems.length}
                columnStatus="preparing"
              >
                <ColumnContent ref={preparingRef}>
                  {preparingItems.map(({ item, order }) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      order={order}
                      onStartPrep={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'preparing',
                          order,
                          item,
                        })
                      }
                      onMarkReady={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'ready',
                          order,
                          item,
                        })
                      }
                      onMarkServed={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'served',
                          order,
                          item,
                        })
                      }
                      onComplete={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'completed',
                          order,
                          item,
                        })
                      }
                      onCancel={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'cancelled',
                          order,
                          item,
                        })
                      }
                      isTransitioning={item.id === transitioningId}
                    />
                  ))}
                  {preparingItems.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: '#9B958F' }}>
                      <Typography sx={{ fontSize: '0.75rem' }}>No items in progress</Typography>
                    </Box>
                  )}
                </ColumnContent>
              </Column>

              <Column
                title="Ready"
                icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                count={readyItems.length}
                columnStatus="ready"
              >
                <ColumnContent ref={readyRef}>
                  {readyItems.map(({ item, order }) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      order={order}
                      onStartPrep={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'preparing',
                          order,
                          item,
                        })
                      }
                      onMarkReady={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'ready',
                          order,
                          item,
                        })
                      }
                      onMarkServed={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'served',
                          order,
                          item,
                        })
                      }
                      onComplete={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'completed',
                          order,
                          item,
                        })
                      }
                      onCancel={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'cancelled',
                          order,
                          item,
                        })
                      }
                      isTransitioning={item.id === transitioningId}
                    />
                  ))}
                  {readyItems.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: '#9B958F' }}>
                      <Typography sx={{ fontSize: '0.75rem' }}>No ready items</Typography>
                    </Box>
                  )}
                </ColumnContent>
              </Column>

              <Column
                title="Served"
                icon={<HistoryIcon sx={{ fontSize: 18 }} />}
                count={servedItems.length}
                columnStatus="served"
              >
                <ColumnContent ref={servedRef}>
                  {servedItems.map(({ item, order }) => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      order={order}
                      onStartPrep={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'preparing',
                          order,
                          item,
                        })
                      }
                      onMarkReady={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'ready',
                          order,
                          item,
                        })
                      }
                      onMarkServed={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'served',
                          order,
                          item,
                        })
                      }
                      onComplete={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'completed',
                          order,
                          item,
                        })
                      }
                      onCancel={() =>
                        updateItemStatusMutation.mutate({
                          itemId: item.id,
                          status: 'cancelled',
                          order,
                          item,
                        })
                      }
                      isTransitioning={item.id === transitioningId}
                    />
                  ))}
                  {servedItems.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4, color: '#9B958F' }}>
                      <Typography sx={{ fontSize: '0.75rem' }}>No served items</Typography>
                    </Box>
                  )}
                </ColumnContent>
              </Column>
            </Box>
            <DragOverlay dropAnimation={null}>
              {activeId ? (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#FFF',
                    border: `2px solid ${ACCENT}`,
                    borderRadius: 0,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    opacity: 0.9,
                    maxWidth: 300,
                  }}
                >
                  {(() => {
                    const found = allItems.find(({ item }) => item.id === activeId);
                    if (!found) return null;
                    return (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          fontWeight: 600,
                        }}
                      >
                        {found.item.product_name} x{found.item.quantity}
                      </Typography>
                    );
                  })()}
                </Box>
              ) : null}
            </DragOverlay>
          </DndContext>
        </Box>

        {/* Floating vertical category filter — right side */}
        <DraggablePanel title="Filters" panelWidth={170}>
          {DEPARTMENTS.map(dept => {
            const Icon = dept.icon;
            const isActive = departmentFilter === dept.id;

            return (
              <Box
                key={dept.id}
                onClick={() => setDepartmentFilter(dept.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.75,
                  py: 1.25,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  bgcolor: isActive ? `${dept.color}15` : 'transparent',
                  border: isActive ? `1px solid ${dept.color}40` : '1px solid transparent',
                  '&:hover': {
                    bgcolor: `${dept.color}08`,
                  },
                }}
              >
                {Icon ? (
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 1.5,
                      bgcolor: isActive ? dept.color : '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {React.cloneElement(Icon as React.ReactElement, {
                      sx: { fontSize: 17, color: isActive ? '#FFF' : '#9B958F' },
                    })}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 1.5,
                      bgcolor: isActive ? dept.color : '#F5F5F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <FilterIcon sx={{ fontSize: 17, color: isActive ? '#FFF' : '#9B958F' }} />
                  </Box>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: isActive ? dept.color : '#1A1A1A',
                      lineHeight: 1.2,
                    }}
                  >
                    {dept.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: '#9B958F',
                    }}
                  >
                    {departmentCounts[dept.id]} items
                  </Typography>
                </Box>
                {isActive && (
                  <Box
                    sx={{
                      width: 3,
                      height: 20,
                      borderRadius: 1.5,
                      bgcolor: dept.color,
                      alignSelf: 'stretch',
                      my: -0.5,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </DraggablePanel>
      </PageSection>
    </Box>
  );
}

interface ColumnProps {
  title: string;
  icon: React.ReactNode;
  count: number;
  columnStatus: string;
  dataTour?: string;
  children: React.ReactNode;
  isLast?: boolean;
}

function Column({ title, icon, count, columnStatus, dataTour, children, isLast }: ColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: columnStatus });
  return (
    <Box
      ref={setNodeRef}
      data-tour={dataTour}
      sx={{
        flex: 1,
        minWidth: 280,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isOver ? 'rgba(44,62,45,0.04)' : '#FAFAF8',
        height: '100%',
        transition: 'all 0.15s ease',
      }}
    >
      <Box
        sx={{
          p: 2,
          border: BORDER,
          borderBottom: BORDER,
          bgcolor: '#F5F3F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ color: '#6B6560' }}>{icon}</Box>
          <Typography
            sx={{
              fontSize: '0.9rem',
              fontWeight: 600,
              color: ACCENT,
            }}
          >
            {title}
          </Typography>
        </Box>
        <Chip
          label={count}
          size="small"
          sx={{
            bgcolor: '#E4DFD9',
            color: '#6B6560',
            borderRadius: '50%',
            minWidth: 26,
            height: 26,
            fontSize: '0.65rem',
            fontWeight: 600,
            border: '1px solid rgba(0,0,0,0.08)',
            '& .MuiChip-label': { px: 0 },
          }}
        />
      </Box>

      {children}
    </Box>
  );
}

const ColumnContent = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
  ({ children }, ref) => {
    return (
      <Box
        ref={ref}
        sx={{
          flex: 1,
          overflow: 'auto',
          pt: 1.5,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            background: '#F5F4F2',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#C4BEB8',
            borderRadius: 0,
          },
        }}
      >
        {children}
      </Box>
    );
  },
);

ColumnContent.displayName = 'ColumnContent';

interface ItemCardProps {
  item: FoodOrderItem;
  order: FoodOrder;
  onStartPrep: () => void;
  onMarkReady: () => void;
  onMarkServed: () => void;
  onComplete: () => void;
  onCancel: () => void;
  isTransitioning?: boolean;
}

function ItemCard({
  item,
  order,
  onStartPrep,
  onMarkReady,
  onMarkServed,
  onComplete,
  onCancel,
  isTransitioning,
}: ItemCardProps) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item.id,
    disabled: item.status === 'served',
  });
  const canPrepare = usePermission('orders.prepare');
  const canMarkReady = usePermission('orders.mark_ready');
  const canMarkServed = usePermission('orders.mark_served');
  const canComplete = usePermission('orders.complete');
  const elapsedMinutes = Math.floor(
    (Date.now() - new Date(item.created_at || order.created_at).getTime()) / 60000,
  );

  const getTimeDisplay = () => {
    if (elapsedMinutes < 60) return `${elapsedMinutes}m`;
    return `${Math.floor(elapsedMinutes / 60)}h ${elapsedMinutes % 60}m`;
  };

  const getTimeWarning = () => {
    if (elapsedMinutes >= 30)
      return {
        icon: <WarningIcon sx={{ fontSize: 14 }} />,
        label: 'Critical delay',
        intensity: 'high',
      };
    if (elapsedMinutes >= 15)
      return {
        icon: <TimeIcon sx={{ fontSize: 14 }} />,
        label: 'Approaching limit',
        intensity: 'medium',
      };
    return { icon: <TimeIcon sx={{ fontSize: 14 }} />, label: 'On track', intensity: 'low' };
  };

  const timeWarning = getTimeWarning();

  const getDepartmentIcon = () => {
    switch (item.department) {
      case 'food':
        return <RestaurantIcon sx={{ fontSize: 14 }} />;
      case 'beverage':
        return <LocalBarIcon sx={{ fontSize: 14 }} />;
      case 'merchandise':
        return <StoreIcon sx={{ fontSize: 14 }} />;
      case 'service':
        return <RoomServiceIcon sx={{ fontSize: 14 }} />;
      default:
        return <RestaurantIcon sx={{ fontSize: 14 }} />;
    }
  };

  const showCancelButton = item.status === 'pending';

  return (
    <>
      <Paper
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        elevation={0}
        sx={{
          p: 1.5,
          border: BORDER,
          borderRadius: 0,
          bgcolor: '#FFF',
          opacity: isTransitioning ? 0.6 : isDragging ? 0.5 : item.status === 'served' ? 0.85 : 1,
          transition: 'opacity 0.2s ease',
          cursor: isTransitioning ? 'wait' : item.status !== 'served' ? 'grab' : 'default',
          touchAction: 'none',
          position: 'relative',
          '&:hover': {
            borderColor: ACCENT,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            opacity: 1,
          },
          '&:hover .complete-badge': {
            opacity: 1,
            transform: 'scale(1)',
          },
        }}
      >
        {item.status === 'served' && canComplete && (
          <Box
            className="complete-badge"
            onClick={e => {
              e.stopPropagation();
              onComplete();
            }}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              px: 1.5,
              py: 0.75,
              bgcolor: ACCENT,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              opacity: 0,
              transform: 'translateY(4px)',
              transition: 'all 0.2s ease',
              zIndex: 3,
              '&:hover': { bgcolor: '#1a2e1b', transform: 'translateY(0) !important' },
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600 }}>Complete</Typography>
          </Box>
        )}
        {isTransitioning && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.7)',
              zIndex: 2,
            }}
          >
            <CircularProgress size={24} sx={{ color: ACCENT }} />
          </Box>
        )}
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: ACCENT,
              }}
            >
              {item.quantity}x
            </Typography>
            <Chip
              icon={getDepartmentIcon()}
              label={item.department || 'Food'}
              size="small"
              sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#FAFAF8', borderRadius: 0 }}
            />
          </Box>
          {item.status !== 'served' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {timeWarning.icon}
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color:
                    timeWarning.intensity === 'high'
                      ? '#D32F2F'
                      : timeWarning.intensity === 'medium'
                        ? '#ED6C02'
                        : '#6B6560',
                  fontWeight: timeWarning.intensity !== 'low' ? 600 : 400,
                }}
              >
                {getTimeDisplay()}
              </Typography>
            </Box>
          )}
          {item.status === 'served' && (
            <Chip
              icon={<ServedIcon sx={{ fontSize: 12 }} />}
              label="Served"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6rem',
                bgcolor: '#E8F5E9',
                color: '#2E7D32',
                borderRadius: 0,
              }}
            />
          )}
        </Box>

        <Typography
          sx={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#1A1A1A',
            mb: 1,
            lineHeight: 1.3,
          }}
        >
          {item.product_name}
        </Typography>

        {/* Order Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.65rem', color: '#9B958F' }}>
            #{order.order_number.split('-').pop()}
          </Typography>
          {order.order_type === 'dine_in' && order.table?.table_number && (
            <Typography
              sx={{
                fontSize: '0.65rem',
                color: '#9B958F',
              }}
            >
              • Table {order.table.table_number}
            </Typography>
          )}
          {order.order_type !== 'dine_in' && (
            <Typography
              sx={{
                fontSize: '0.65rem',
                color: '#9B958F',
              }}
            >
              • {order.order_type === 'takeout' ? 'Takeout' : 'Delivery'}
            </Typography>
          )}
          {order.guest_name && (
            <Typography
              sx={{
                fontSize: '0.65rem',
                color: '#9B958F',
              }}
            >
              • {order.guest_name}
            </Typography>
          )}
        </Box>

        {/* Notes if any */}
        {item.preparation_notes && (
          <Box
            data-tour="dept-tabs"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.5,
              mb: 1.5,
              p: 1,
              bgcolor: '#FAFAF8',
            }}
          >
            <NotesIcon sx={{ fontSize: 12, color: '#9B958F' }} />
            <Typography
              sx={{
                fontSize: '0.65rem',
                color: '#6B6560',
                fontStyle: 'italic',
              }}
            >
              {item.preparation_notes}
            </Typography>
          </Box>
        )}

        {item.modifiers && item.modifiers.length > 0 && (
          <Box sx={{ mb: 1.5, pl: 0.5 }}>
            {item.modifiers.map((mod, i) => (
              <Typography
                key={i}
                sx={{
                  fontSize: '0.6rem',
                  color: '#6B6560',
                }}
              >
                + {mod.name}
                {mod.price > 0 && (
                  <Box component="span" sx={{ color: ACCENT, fontWeight: 600, ml: 0.5 }}>
                    +{mod.price.toLocaleString()}
                  </Box>
                )}
              </Typography>
            ))}
          </Box>
        )}

        {showCancelButton && (
          <IconButton
            onClick={() => setCancelDialogOpen(true)}
            size="small"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              color: '#9B958F',
              borderRadius: 0,
              '&:hover': { color: '#D32F2F', bgcolor: '#FFF5F5' },
            }}
          >
            <CancelIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Paper>

      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 0, border: BORDER } }}
      >
        <DialogTitle
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          Cancel Item
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
            Cancel <strong>{item.product_name}</strong> from order #
            {order.order_number.split('-').pop()}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            sx={{
              borderRadius: 0,

              textTransform: 'none',
            }}
          >
            Keep Item
          </Button>
          <Button
            onClick={() => {
              onCancel();
              setCancelDialogOpen(false);
            }}
            variant="contained"
            sx={{
              bgcolor: '#9B958F',
              borderRadius: 0,

              textTransform: 'none',
              '&:hover': { bgcolor: '#6B6560' },
            }}
          >
            Cancel Item
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SplitDisplay;
